/* eslint-disable @typescript-eslint/naming-convention */
import { APP_ROUTES } from '@core/routes';
import { COLLABORATION_ANCHOR_ID, COLLABORATION_TAB_PARAM } from '@features/listing-details/constants/anchors';
import { BaseNotification } from '@features/notifications/models/base/base-notification';
import { CustomerBaseNotification } from '@features/notifications/models/base/customer-base-notification';
import { EventAction, NotificationEntityType } from 'src/.graphql/types';

export const NOTIFICATION_TITLE_MAP: Partial<Record<EventAction, string>> = {
    'APPOINTMENT_ACCEPTED': 'NOTIFICATION_MAPPING.TITLES.APPOINTMENT_CONFIRMED',
    'APPOINTMENT_CREATED': 'NOTIFICATION_MAPPING.TITLES.APPOINTMENT_REQUESTED',
    'APPOINTMENT_DECLINED': 'NOTIFICATION_MAPPING.TITLES.APPOINTMENT_DECLINED',
    'APPOINTMENT_UPDATED': 'NOTIFICATION_MAPPING.TITLES.APPOINTMENT_UPDATED',

    'COMMENT_CREATED': 'NOTIFICATION_MAPPING.TITLES.MESSAGE_ADDED',
    'COMMENT_UPDATED': 'NOTIFICATION_MAPPING.TITLES.MESSAGE_MODIFIED',

    'LISTING_ACTIVITY_ADDED_TO_PORTFOLIO': 'NOTIFICATION_MAPPING.TITLES.LISTING',
    'EXTERNAL_LISTING_ADDED': 'NOTIFICATION_MAPPING.TITLES.EXTERNAL_LISTING',
    'LISTING_ACTIVITY_DISLIKED': 'NOTIFICATION_MAPPING.TITLES.REACTION',
    'LISTING_ACTIVITY_LIKED': 'NOTIFICATION_MAPPING.TITLES.REACTION',
    'LISTING_ACTIVITY_REMOVED': 'NOTIFICATION_MAPPING.TITLES.REACTION',
    'LISTING_ACTIVITY_SHOWN': 'NOTIFICATION_MAPPING.TITLES.VISITED',

    'NEW_MATCH_ADDED': 'NOTIFICATION_MAPPING.TITLES.NEW_MATCH',

    'EMAIL_READ': 'NOTIFICATION_MAPPING.TITLES.EMAIL',
    'REPORT_READ': 'NOTIFICATION_MAPPING.TITLES.REPORT',

    'CUSTOMER_CREATES_SAVED_SEARCH': 'NOTIFICATION_MAPPING.TITLES.SEARCH',

    'CUSTOMER_REGISTERED': 'NOTIFICATION_MAPPING.TITLES.REGISTERED',
    'CUSTOMER_INVITED_CHILD_CUSTOMER': 'NOTIFICATION_MAPPING.TITLES.CHILD_CUSTOMER',
    'CHILD_CUSTOMER_REGISTERED': 'NOTIFICATION_MAPPING.TITLES.CHILD_REGISTERED',
    'CUSTOMER_SHARED': 'NOTIFICATION_MAPPING.TITLES.SHARED',
    'CUSTOMER_SHARE_DELETED': 'NOTIFICATION_MAPPING.TITLES.SHARE_DELETED',
    'CUSTOMER_SHARE_SELF_DELETED': 'NOTIFICATION_MAPPING.TITLES.SHARE_SELF_DELETED',
    'CUSTOMER_SHARE_UPDATED': 'NOTIFICATION_MAPPING.TITLES.SHARE_UPDATED',

    'OPEN_HOUSE_CREATED': 'NOTIFICATION_MAPPING.TITLES.OPEN_HOUSE',

    'LISTING_STALE': 'NOTIFICATION_MAPPING.TITLES.UPDATES',
    'LISTING_EXPIRING': 'NOTIFICATION_MAPPING.TITLES.EXPIRE',

    'FAVORITE_LISTING_PRICE_CHANGED': 'NOTIFICATION_MAPPING.TITLES.FAVORITE_PRICE_CHANGED',
    'FAVORITE_LISTING_STATUS_CHANGED': 'NOTIFICATION_MAPPING.TITLES.FAVORITE_STATUS_CHANGED',
    'FAVORITE_LISTING_MAINT_CC_CHANGED': 'NOTIFICATION_MAPPING.TITLES.FAVORITE_MAINT_CC_CHANGED',
};

export const REACTION_TITLE_MAP: Partial<Record<EventAction, string>> = {
    'LISTING_ACTIVITY_DISLIKED': 'NOTIFICATION_MAPPING.TITLES.DISLIKED',
    'LISTING_ACTIVITY_LIKED': 'NOTIFICATION_MAPPING.TITLES.LIKED',
    'LISTING_ACTIVITY_REMOVED': 'NOTIFICATION_MAPPING.TITLES.REMOVED',
};

export const REPORT_ENTITY_TYPE_MAP: Partial<Record<NotificationEntityType, (isCorcoran: boolean) => string>> = {
    'GRID_REPORT': _ => 'NOTIFICATION_MAPPING.REPORT_TYPES.GRID_REPORT',
    'SUMMARY_REPORT': _ => 'NOTIFICATION_MAPPING.REPORT_TYPES.SUMMARY_REPORT',
    'DETAIL_REPORT': _ => 'NOTIFICATION_MAPPING.REPORT_TYPES.DETAIL_REPORT',
    'FACT_SHEET_REPORT': isCorcoran => isCorcoran
        ? 'NOTIFICATION_MAPPING.REPORT_TYPES.SHOW_SHEET_REPORT'
        : 'NOTIFICATION_MAPPING.REPORT_TYPES.FACT_SHEET_REPORT',
    'CMA_REPORT': _ => 'NOTIFICATION_MAPPING.REPORT_TYPES.CMA_REPORT',
    'COMPARABLE_SEARCH_REPORT': _ => 'NOTIFICATION_MAPPING.REPORT_TYPES.CMA_REPORT',
    'OPEN_HOUSE_REPORT': _ => 'NOTIFICATION_MAPPING.REPORT_TYPES.OPEN_HOUSE_REPORT',
    'MEDIA_REPORT': _ => 'NOTIFICATION_MAPPING.REPORT_TYPES.MEDIA_REPORT',
    'COMPARISON_REPORT': _ => 'NOTIFICATION_MAPPING.REPORT_TYPES.COMPARISON_REPORT',
    'MARKET_ACTIVITY_REPORT': _ => 'NOTIFICATION_MAPPING.REPORT_TYPES.MARKET_ACTIVITY_REPORT',
    'AGENT_ANALYTICS_REPORT': _ => 'NOTIFICATION_MAPPING.REPORT_TYPES.AGENT_ANALYTICS_REPORT'
};

export const CUSTOMER_NOTIFICATIONS: EventAction[] = [
    'APPOINTMENT_ACCEPTED',
    'APPOINTMENT_CREATED',
    'APPOINTMENT_DECLINED',
    'APPOINTMENT_UPDATED',
    'COMMENT_CREATED',
    'COMMENT_UPDATED',

    'LISTING_ACTIVITY_ADDED_TO_PORTFOLIO',
    'EXTERNAL_LISTING_ADDED',
    'LISTING_ACTIVITY_DISLIKED',
    'LISTING_ACTIVITY_LIKED',
    'LISTING_ACTIVITY_SHOWN',

    'EMAIL_READ',
    'REPORT_READ',
    'CUSTOMER_CREATES_SAVED_SEARCH',
    'CUSTOMER_REGISTERED',
    'CUSTOMER_INVITED_CHILD_CUSTOMER',
    'CHILD_CUSTOMER_REGISTERED',

    'FAVORITE_LISTING_PRICE_CHANGED',
    'FAVORITE_LISTING_STATUS_CHANGED',
    'FAVORITE_LISTING_MAINT_CC_CHANGED',
];

export const ALWAYS_AGENT_NOTIFICATION: EventAction[] = [
    'CUSTOMER_SHARED',
    'CUSTOMER_SHARE_UPDATED',
    'CUSTOMER_SHARE_DELETED',
    'CUSTOMER_SHARE_SELF_DELETED',
    'LISTING_STALE',
    'LISTING_EXPIRING',
    'MARKET_ACTIVITY_REPORT_GENERATED',
];

export const REGISTERED_NOTIFICATIONS: EventAction[] = [
    'CUSTOMER_REGISTERED',
    'CHILD_CUSTOMER_REGISTERED',
];

export const DATE_NOTIFICATIONS: EventAction[] = [
    'OPEN_HOUSE_CREATED',
];

export const NOTIFICATION_ICON_MAP: Partial<Record<EventAction, string>> = {
    'NEW_MATCH_ADDED': 'new-star',
    'OPEN_HOUSE_CREATED': 'new-oh',
    'MARKET_ACTIVITY_REPORT_GENERATED': 'new-market-analytics',
    'LISTING_STALE': 'new-report-fill',
    'LISTING_EXPIRING': 'new-report-fill',
};

export const SUBTITLE_FIELD_MAP: Partial<Record<EventAction, string>> = {
    'CUSTOMER_CREATES_SAVED_SEARCH': 'savedSearchName',
    'CUSTOMER_SHARED': 'customerFullName',
    'CUSTOMER_SHARE_UPDATED': 'customerFullName',
    'CUSTOMER_SHARE_DELETED': 'customerFullName',
    'CUSTOMER_SHARE_SELF_DELETED': 'customerFullName',
    'CHILD_CUSTOMER_REGISTERED': 'childCustomerFullName',
};

export const SUBTITLE_TEXT_MAP: Record<string, string> = {
    'CUSTOMER_REGISTERED': 'NOTIFICATION_MAPPING.SUBTITLES.REGISTERED',
};

export const ADDRESS_SUBTITLE_NOTIFICATIONS: EventAction[] = [
    'APPOINTMENT_ACCEPTED',
    'APPOINTMENT_CREATED',
    'APPOINTMENT_DECLINED',
    'APPOINTMENT_UPDATED',
    'COMMENT_CREATED',
    'COMMENT_UPDATED',
    'EXTERNAL_LISTING_ADDED',
    'LISTING_ACTIVITY_ADDED_TO_PORTFOLIO',
    'LISTING_ACTIVITY_DISLIKED',
    'LISTING_ACTIVITY_LIKED',
    'LISTING_ACTIVITY_SHOWN',
    'LISTING_STALE',
    'LISTING_EXPIRING',
    'OPEN_HOUSE_CREATED',
    'FAVORITE_LISTING_MAINT_CC_CHANGED',
    'FAVORITE_LISTING_PRICE_CHANGED',
    'FAVORITE_LISTING_STATUS_CHANGED'
];

//TODO: add correct icons names
export const SUBTITLE_ICON_MAP: Partial<Record<EventAction, string>> = {
    'APPOINTMENT_ACCEPTED': 'new-date-today',
    'APPOINTMENT_CREATED': 'new-date-today',
    'APPOINTMENT_DECLINED': 'new-date-today',
    'APPOINTMENT_UPDATED': 'new-date-today',

    'COMMENT_CREATED': 'new-comments',
    'COMMENT_UPDATED': 'new-comments',

    'LISTING_ACTIVITY_ADDED_TO_PORTFOLIO': 'pick',
    'EXTERNAL_LISTING_ADDED': 'pick',
    'LISTING_ACTIVITY_DISLIKED': 'dislike',
    'LISTING_ACTIVITY_LIKED': 'like',
    'LISTING_ACTIVITY_SHOWN': 'shown',

    'NEW_MATCH_ADDED': 'new-star-fill',

    'EMAIL_READ': 'new-email-open',
    'REPORT_READ': 'new-report-fill',

    'CUSTOMER_CREATES_SAVED_SEARCH': 'new-saved-search',

    'CUSTOMER_REGISTERED': 'new-approve',
    'CUSTOMER_INVITED_CHILD_CUSTOMER': 'new-user-fill-add',
    'CHILD_CUSTOMER_REGISTERED': 'new-approve',
    'CUSTOMER_SHARED': 'new-user-fill-share',
    'CUSTOMER_SHARE_DELETED': 'new-user-fill-share',
    'CUSTOMER_SHARE_SELF_DELETED': 'new-user-fill-share',
    'CUSTOMER_SHARE_UPDATED': 'new-user-fill-share',
};

export const SUBTITLE_ICON_CLASS_MAP: Partial<Record<EventAction, string>> = {
    'EXTERNAL_LISTING_ADDED': 'pnyc-pick',
    'LISTING_ACTIVITY_ADDED_TO_PORTFOLIO': 'pnyc-pick',
    'LISTING_ACTIVITY_DISLIKED': 'pnyc-dislike',
    'LISTING_ACTIVITY_LIKED': 'pnyc-like',
    'LISTING_ACTIVITY_SHOWN': 'pnyc-shown',
    'CHILD_CUSTOMER_REGISTERED': 'pnyc-approve',
    'CUSTOMER_REGISTERED': 'pnyc-approve',
};

function collaborationLink(notification: BaseNotification, tab: 'messages' | 'appointments'): string {
    return `${APP_ROUTES.LISTING_DETAILS}/${notification.listingId}` +
        `?${COLLABORATION_TAB_PARAM}=${tab}#${COLLABORATION_ANCHOR_ID}`;
}

export const REDIRECT_LINKS_MAP: Record<string, (notification: BaseNotification) => string | null> = {
    'APPOINTMENT_ACCEPTED': notification => collaborationLink(notification, 'appointments'),
    'APPOINTMENT_CREATED': notification => collaborationLink(notification, 'appointments'),
    'APPOINTMENT_DECLINED': notification => collaborationLink(notification, 'appointments'),
    'APPOINTMENT_UPDATED': notification => collaborationLink(notification, 'appointments'),

    'COMMENT_CREATED': notification => collaborationLink(notification, 'messages'),
    'COMMENT_UPDATED': notification => collaborationLink(notification, 'messages'),

    'LISTING_ACTIVITY_ADDED_TO_PORTFOLIO': notification => `${APP_ROUTES.LISTING_DETAILS}/${notification.listingId}`,
    'EXTERNAL_LISTING_ADDED': notification => `${APP_ROUTES.CUSTOMER}/${(notification as CustomerBaseNotification).customerId}`,
    'LISTING_ACTIVITY_DISLIKED': notification => `${APP_ROUTES.LISTING_DETAILS}/${notification.listingId}`,
    'LISTING_ACTIVITY_LIKED': notification => `${APP_ROUTES.LISTING_DETAILS}/${notification.listingId}`,
    'LISTING_ACTIVITY_SHOWN': notification => `${APP_ROUTES.LISTING_DETAILS}/${notification.listingId}`,

    'NEW_MATCH_ADDED': notification => (notification as CustomerBaseNotification)?.customerId != null
        ? `${APP_ROUTES.CUSTOMER}/${(notification as CustomerBaseNotification).customerId}`
        : APP_ROUTES.SAVED_SEARCHES_FOLDER_VIEW,

    'EMAIL_READ': notification => `${APP_ROUTES.CUSTOMER}/${(notification as CustomerBaseNotification).customerId}`,
    'REPORT_READ': notification => `${APP_ROUTES.CUSTOMER}/${(notification as CustomerBaseNotification).customerId}`,

    'CUSTOMER_CREATES_SAVED_SEARCH': notification => `${APP_ROUTES.CUSTOMER}/${(notification as CustomerBaseNotification).customerId}`,

    'CUSTOMER_REGISTERED': notification => `${APP_ROUTES.CUSTOMER}/${(notification as CustomerBaseNotification).customerId}`,
    'CUSTOMER_INVITED_CHILD_CUSTOMER': notification => `${APP_ROUTES.CUSTOMER}/${(notification as CustomerBaseNotification).customerId}`,
    'CHILD_CUSTOMER_REGISTERED': notification => `${APP_ROUTES.CUSTOMER}/${(notification as CustomerBaseNotification).customerId}`,
    'CUSTOMER_SHARED': notification => `${APP_ROUTES.CUSTOMER}/${(notification as CustomerBaseNotification).customerId}`,
    'CUSTOMER_SHARE_DELETED': _ => null,
    'CUSTOMER_SHARE_SELF_DELETED': notification => `${APP_ROUTES.CUSTOMER}/${(notification as CustomerBaseNotification).customerId}`,
    'CUSTOMER_SHARE_UPDATED': notification => `${APP_ROUTES.CUSTOMER}/${(notification as CustomerBaseNotification).customerId}`,

    'OPEN_HOUSE_CREATED': notification => `${APP_ROUTES.LISTING_DETAILS}/${notification.entityId}`,

    'MARKET_ACTIVITY_REPORT_GENERATED': _ => APP_ROUTES.MARKET_ACTIVITY,

    'LISTING_STALE': _ => APP_ROUTES.AGENT_HUB,
    'LISTING_EXPIRING': _ => APP_ROUTES.AGENT_HUB,

    'FAVORITE_LISTING_PRICE_CHANGED': notification => {
        const { customerId } = (notification as CustomerBaseNotification);

        return customerId
            ? `${APP_ROUTES.FOLDER_VIEW}/system/liked?customerId=${customerId}`
            : `${APP_ROUTES.LISTING_DETAILS}/${notification.listingId}`;
    },
    'FAVORITE_LISTING_STATUS_CHANGED': notification => {
        const { customerId } = (notification as CustomerBaseNotification);

        return customerId
            ? `${APP_ROUTES.FOLDER_VIEW}/system/liked?customerId=${customerId}`
            : `${APP_ROUTES.LISTING_DETAILS}/${notification.listingId}`;
    },
    'FAVORITE_LISTING_MAINT_CC_CHANGED': notification => {
        const { customerId } = (notification as CustomerBaseNotification);

        return customerId
            ? `${APP_ROUTES.FOLDER_VIEW}/system/liked?customerId=${customerId}`
            : `${APP_ROUTES.LISTING_DETAILS}/${notification.listingId}`;
    },
};

export const SELECT_CUSTOMER_ON_REDIRECT_NOTIFICATIONS: EventAction[] = [
    'APPOINTMENT_ACCEPTED',
    'APPOINTMENT_CREATED',
    'APPOINTMENT_DECLINED',
    'APPOINTMENT_UPDATED',
    'COMMENT_CREATED',
    'COMMENT_UPDATED',
];
